import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  List,
  ListItem,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { type Nationality, type Patient } from '../../model/patient'
import { getPatientService, getQueueService } from '../../services/Services'
import messages from './messages'
import CountrySelect from '../../components/common/form/CountrySelect'

interface AddPatientPanelProps {
  doctorId: string
}

// --- Existing Patient Tab ---

function ExistingPatientTab({ doctorId }: { doctorId: string }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<Patient[]>([])
  const [searching, setSearching] = useState(false)
  const [adding, setAdding] = useState(false)
  const toast = useToast()
  const { formatMessage } = useIntl()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults([])
      return
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const patients = await getPatientService().searchPatients({
          searchTerm,
          ownerId: doctorId
        })
        setResults(patients)
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [searchTerm, doctorId])

  const handleSelect = async (patient: Patient) => {
    setAdding(true)
    try {
      await getQueueService().addToQueue({
        doctorId,
        patientId: patient.id,
        patientName: patient.name
      })
      setSearchTerm('')
      setResults([])
      toast({
        title: formatMessage(messages.patientAddedToQueue),
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch {
      toast({
        title: formatMessage(messages.failedToAddToQueue),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setAdding(false)
    }
  }

  return (
    <Box>
      <FormControl>
        <FormLabel>
          {formatMessage(messages.searchPatientPlaceholder)}
        </FormLabel>
        <Input
          placeholder={formatMessage(messages.typeAtLeast2Chars)}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
          }}
          autoComplete="off"
        />
      </FormControl>

      {searching && (
        <Flex justify="center" mt={3}>
          <Spinner size="sm" />
        </Flex>
      )}

      {!searching && results.length > 0 && (
        <List
          mt={2}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          overflow="hidden"
        >
          {results.map((patient) => (
            <ListItem
              key={patient.id}
              px={4}
              py={3}
              cursor={adding ? 'not-allowed' : 'pointer'}
              _hover={{ bg: 'blue.50' }}
              borderBottom="1px solid"
              borderColor="gray.100"
              _last={{ borderBottom: 'none' }}
              onClick={() => {
                if (!adding) {
                  handleSelect(patient)
                }
              }}
            >
              <Flex justify="space-between" align="center">
                <Text fontWeight="medium">{patient.name}</Text>
                <Text fontSize="sm" color="gray.500">
                  {patient.phoneNumber || '—'}
                </Text>
              </Flex>
            </ListItem>
          ))}
        </List>
      )}

      {!searching && searchTerm.length >= 2 && results.length === 0 && (
        <Text mt={3} color="gray.500" fontSize="sm" textAlign="center">
          {formatMessage(messages.noPatientsFound)}
        </Text>
      )}
    </Box>
  )
}

// --- New Patient Tab ---

interface NewPatientForm {
  name: string
  phoneNumber: string
  dateOfBirth: string
  sex: 'M' | 'F' | ''
  nationality: Nationality
}

const emptyNewPatientForm = (): NewPatientForm => ({
  name: '',
  phoneNumber: '',
  dateOfBirth: '',
  sex: '',
  nationality: 'EG'
})

function NewPatientTab({ doctorId }: { doctorId: string }) {
  const [form, setForm] = useState<NewPatientForm>(emptyNewPatientForm)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toast = useToast()
  const { formatMessage } = useIntl()

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError(formatMessage(messages.nameRequired))
      return
    }
    if (!form.dateOfBirth) {
      setError(formatMessage(messages.dateOfBirthRequired))
      return
    }
    if (!form.sex) {
      setError(formatMessage(messages.sexRequired))
      return
    }

    setLoading(true)
    setError(null)

    try {
      const patient = await getPatientService().createPatient({
        ownerId: doctorId,
        name: form.name.trim(),
        phoneNumber: form.phoneNumber.trim(),
        dateOfBirth: form.dateOfBirth,
        sex: form.sex || 'M',
        nationality: form.nationality,
        address: ''
      })

      await getQueueService().addToQueue({
        doctorId,
        patientId: patient.id,
        patientName: patient.name
      })

      toast({
        title: formatMessage(messages.patientAddedToQueue),
        status: 'success',
        duration: 2000,
        isClosable: true
      })

      setForm(emptyNewPatientForm())
    } catch {
      setError(formatMessage(messages.failedToCreatePatient))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <Flex direction="column" gap={4}>
        <FormControl isRequired>
          <FormLabel>{formatMessage(messages.name)}</FormLabel>
          <Input
            name="name"
            placeholder={formatMessage(messages.fullNamePlaceholder)}
            value={form.name}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl>
          <FormLabel>{formatMessage(messages.phone)}</FormLabel>
          <Input
            name="phoneNumber"
            placeholder="+1 234 567 8900"
            value={form.phoneNumber}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>{formatMessage(messages.dateOfBirth)}</FormLabel>
          <Input
            name="dateOfBirth"
            type="date"
            value={form.dateOfBirth}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>{formatMessage(messages.sex)}</FormLabel>
          <Select
            name="sex"
            placeholder={formatMessage(messages.selectSexPlaceholder)}
            value={form.sex}
            onChange={handleChange}
          >
            <option value="M">{formatMessage(messages.male)}</option>
            <option value="F">{formatMessage(messages.female)}</option>
          </Select>
        </FormControl>

        <CountrySelect
          label={formatMessage({
            id: 'patientForm.nationality',
            defaultMessage: 'Nationality'
          })}
          value={form.nationality}
          onChange={(nationality) => {
            setForm((current) => ({ ...current, nationality }))
            setError(null)
          }}
        />

        {error && (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        )}

        <Button
          type="submit"
          colorScheme="blue"
          isLoading={loading}
          loadingText={formatMessage(messages.creatingPatient)}
        >
          {formatMessage(messages.addPatient)}
        </Button>
      </Flex>
    </Box>
  )
}

// --- Main Component ---

export default function AddPatientPanel({ doctorId }: AddPatientPanelProps) {
  const { formatMessage } = useIntl()

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      p={6}
      shadow="sm"
    >
      <Text fontSize="lg" fontWeight="semibold" mb={4} color="gray.700">
        {formatMessage(messages.addPatientToQueue)}
      </Text>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>{formatMessage(messages.existingPatient)}</Tab>
          <Tab>{formatMessage(messages.newPatient)}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <ExistingPatientTab doctorId={doctorId} />
          </TabPanel>
          <TabPanel px={0}>
            <NewPatientTab doctorId={doctorId} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}
